/**
 * buildItem — convert a MetadataResponse into a fully resolved Item ready
 * for the player, the queue, and persistence.
 *
 * See data-model.md → Item for the target shape and the validation rules
 * that apply.
 */

import type { Item, ItemLicense, MetadataResponse } from "../../types.ts";
import { pickFile } from "./pickFile.ts";
import { streamUrl } from "./streamUrl.ts";
import { computeDisplayAspectRatio } from "../player/aspectRatio.ts";

function parseLicense(meta: MetadataResponse["metadata"]): ItemLicense | undefined {
  if (meta.licenseurl) return { kind: "url", value: meta.licenseurl };
  if (meta.rights) return { kind: "statement", value: meta.rights };
  return undefined;
}

function parseDurationSeconds(length?: string): number {
  if (!length) return 0;
  // archive.org returns lengths in a few shapes: "5760.00", "5760",
  // or "1:36:00" (HH:MM:SS / MM:SS).
  if (/^[\d.]+$/.test(length)) {
    const n = parseFloat(length);
    return Number.isFinite(n) ? n : 0;
  }
  const parts = length.split(":").map((p) => parseInt(p, 10));
  if (parts.some((p) => !Number.isFinite(p))) return 0;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 1) return parts[0];
  return 0;
}

export function buildItem(metadata: MetadataResponse): Item {
  const picked = pickFile(metadata);
  const id = metadata.metadata.identifier;
  const detailsUrl = `https://archive.org/details/${id}`;
  const thumbUrl = `https://archive.org/services/img/${id}`;
  const title = metadata.metadata.title ?? id;
  const year = metadata.metadata.year;
  const uploader = metadata.metadata.uploader;
  const license = parseLicense(metadata.metadata);

  if (picked.isEmbedFallback) {
    return {
      identifier: id,
      title,
      year,
      uploader,
      license,
      durationSeconds: 0,
      thumbUrl,
      streamUrl: `https://archive.org/embed/${id}`,
      encodedWidth: 0,
      encodedHeight: 0,
      displayAspectRatio: 4 / 3,
      detailsUrl,
      isEmbedFallback: true,
    };
  }

  const file = picked.file;
  const w = file.width ? parseInt(file.width, 10) : 0;
  const h = file.height ? parseInt(file.height, 10) : 0;

  return {
    identifier: id,
    title,
    year,
    uploader,
    license,
    durationSeconds: parseDurationSeconds(file.length),
    thumbUrl,
    streamUrl: streamUrl(metadata, file),
    encodedWidth: Number.isFinite(w) ? w : 0,
    encodedHeight: Number.isFinite(h) ? h : 0,
    displayAspectRatio: computeDisplayAspectRatio(file),
    detailsUrl,
    isEmbedFallback: false,
  };
}
